import { API } from "@/app/api/BaseAPI";
import { ModuleEntity, ModuleVersionEntity, CommentEntity, LikeResponse } from "@/app/entity/ModuleEntity";

export interface PaginatedModules {
  items: ModuleEntity[];
  total: number;
  page: number;
  pageSize: number;
}

export class ModuleAPI {
  getPluginList(page: number = 1, pageSize: number = 20): Promise<PaginatedModules> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withPath('/plugin/list')
        .withParams({ page, pageSize })
        .withMethod("GET")
        .request()
        .then((response) => {
          const data = response.data.data;
          resolve({
            items: data?.items || data || [],
            total: data?.total ?? 0,
            page: data?.page ?? page,
            pageSize: data?.pageSize ?? pageSize,
          });
        }, (reason) => {
          reject(reason);
        });
    });
  }

  getPlugin(identifyString: string): Promise<ModuleEntity> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withPath('/plugin/get')
        .withParams({ identifyString })
        .withMethod("GET")
        .request()
        .then((response) => {
          resolve(response.data.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  getVersions(pluginId: number): Promise<ModuleVersionEntity[]> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withPath(`/plugin/${pluginId}/versions`)
        .withMethod("GET")
        .request()
        .then((response) => {
          resolve(response.data.data || []);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  getLatestVersion(pluginId: number): Promise<string> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withPath(`/plugin/${pluginId}/latest`)
        .withMethod("GET")
        .request()
        .then((response) => {
          resolve(response.data.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  getVersionsDetail(pluginId: number): Promise<ModuleVersionEntity[]> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withPath(`/plugin/${pluginId}/versions-detail`)
        .withMethod("GET")
        .request()
        .then((response) => {
          resolve(response.data.data || []);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  getComments(pluginId: number, page: number = 1, pageSize: number = 10): Promise<{ items: CommentEntity[]; total: number }> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withPath(`/plugin/${pluginId}/comments`)
        .withParams({ page, pageSize })
        .withMethod("GET")
        .request()
        .then((response) => {
          const data = response.data.data;
          resolve({ items: data?.items || [], total: data?.total || 0 });
        }, (reason) => {
          reject(reason);
        });
    });
  }

  getLikes(pluginId: number, withAuth: boolean = false): Promise<LikeResponse> {
    const api = new API();
    if (withAuth) {
      api.withToken();
    }
    return new Promise((resolve, reject) => {
      api
        .withPath(`/plugin/${pluginId}/likes`)
        .withMethod("GET")
        .request()
        .then((response) => {
          resolve(response.data.data || { count: 0, liked: false });
        }, (reason) => {
          reject(reason);
        });
    });
  }

  postComment(pluginId: number, content: string): Promise<CommentEntity> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withToken()
        .withPath(`/plugin/${pluginId}/comments`)
        .withMethod("POST")
        .withBody({ content })
        .request()
        .then((response) => {
          resolve(response.data.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }

  toggleLike(pluginId: number): Promise<LikeResponse> {
    const api = new API();
    return new Promise((resolve, reject) => {
      api
        .withToken()
        .withPath(`/plugin/${pluginId}/like`)
        .withMethod("POST")
        .request()
        .then((response) => {
          resolve(response.data.data);
        }, (reason) => {
          reject(reason);
        });
    });
  }
}
